// ============================================================
//  PDF-Ausgabe (Gate-Dokument, Plan). Gemeinsamer Helfer, damit alle Exporte gleich aussehen:
//  Kopf mit Titel und Untertitel, Abschnitte mit Zeilen, Tabellen, Fußzeile mit Quelle und Datum.
//  jsPDF-Standardschrift (Helvetica, WinAnsi) kann Umlaute — keine Transliteration.
// ============================================================
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

export interface PdfDoc { doc: jsPDF; y: number }

const M = 14; // Rand mm

export function pdfStart(title: string, subtitle?: string): PdfDoc {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  doc.setFont("helvetica", "bold"); doc.setFontSize(16); doc.text(title, M, 18);
  doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(90);
  if (subtitle) doc.text(subtitle, M, 24);
  doc.setTextColor(0);
  return { doc, y: 32 };
}

function ensureSpace(p: PdfDoc, needed: number) {
  const h = p.doc.internal.pageSize.getHeight();
  if (p.y + needed > h - M) { p.doc.addPage(); p.y = M + 4; }
}

export function pdfHeading(p: PdfDoc, text: string) {
  ensureSpace(p, 12);
  p.doc.setFont("helvetica", "bold"); p.doc.setFontSize(12); p.doc.text(text, M, p.y); p.y += 6;
  p.doc.setFont("helvetica", "normal"); p.doc.setFontSize(10);
}

export function pdfLines(p: PdfDoc, lines: string[]) {
  const w = p.doc.internal.pageSize.getWidth() - 2 * M;
  for (const l of lines) {
    const parts = p.doc.splitTextToSize(l, w) as string[];
    ensureSpace(p, parts.length * 5 + 1);
    p.doc.text(parts, M, p.y); p.y += parts.length * 5 + 1;
  }
  p.y += 2;
}

/** Zwei-Spalten-Kennzahlen: [Label, Wert]. */
export function pdfKpis(p: PdfDoc, rows: [string, string][]) {
  autoTable(p.doc, { startY: p.y, margin: { left: M, right: M }, theme: "plain", styles: { fontSize: 10, cellPadding: 1.2 }, columnStyles: { 0: { textColor: 90, cellWidth: 70 }, 1: { fontStyle: "bold" } }, body: rows });
  p.y = (p.doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 4;
}

export function pdfTable(p: PdfDoc, head: string[], rows: (string | number)[][], opts?: { fontSize?: number }) {
  autoTable(p.doc, { startY: p.y, margin: { left: M, right: M }, head: [head], body: rows, styles: { fontSize: opts?.fontSize ?? 8.5, cellPadding: 1.4 }, headStyles: { fillColor: [36, 64, 122], textColor: 255 }, alternateRowStyles: { fillColor: [245, 247, 250] } });
  p.y = (p.doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 5;
}

export function pdfSave(p: PdfDoc, filename: string, footer = "RISE License Cockpit · mindsquare AG · Beispieldaten, kein Produktivsystem") {
  const n = p.doc.getNumberOfPages();
  const w = p.doc.internal.pageSize.getWidth(), h = p.doc.internal.pageSize.getHeight();
  for (let i = 1; i <= n; i++) {
    p.doc.setPage(i); p.doc.setFontSize(8); p.doc.setTextColor(120);
    p.doc.text(`${footer} · ${new Date().toLocaleDateString("de-DE")}`, M, h - 8);
    p.doc.text(`Seite ${i} / ${n}`, w - M, h - 8, { align: "right" });
    p.doc.setTextColor(0);
  }
  p.doc.save(filename);
}
